import { nyxHttp } from "@/services/api-service";
import {
  getAccountId,
  getAuthToken,
  getLoggedInUserRole,
  getBundleId,
  getCurrentUserEmail,
  getNeoSignupAppUrl,
  getOrderDetailsOfProduct,
  getUrl,
  isMobile,
  PRODUCTS,
  openURL,
  returnObjectIfValueExists,
  getLoggedInUserName,
} from ".";
import { generateCrid } from "./api";
import { ADMIN_ROLE } from "./const";
import store from "@/store";

// Bit state wise position
export const apiSiteStateBits = [
  "isPublished",
  "isPublishInProgress",
  "isContactFormEnabled",
  "hasEverBeenPublished",
  "isDraft",
  "v1Site",
  "devOpsPreview",
];

export const fetchNeoWebsiteDetails = async () => {
  const bid = getBundleId();
  const soid = store.getters["site/orderId"];

  try {
    /* `nyxHttp` attaches the auth token and resolves the flockmail base URL per region, so this no
       longer hand-builds headers or reads `VUE_APP_API_BASE_URL` (which had no EU variant, sending
       EU users to the non-EU host). Its response interceptor already unwraps `response.data`. */
    return await nyxHttp.get("neo/website/siteAttrs", {
      params: { bid, soid, crid: generateCrid("WIR") },
    });
  } catch (e) {
    console.error("Error while fetching neo website details", e);
  }
};

export const readFromBitMask = orderedKeys => {
  return value =>
    orderedKeys.reduce(
      (acc, key, index) =>
        // eslint-disable-next-line no-bitwise
        ({ ...acc, [key]: Boolean(value & (2 ** index)) }),
      {}
    );
};

export const fromSiteState = readFromBitMask(apiSiteStateBits);

// function to derive bitMask flags for each site version
export const getNeoWebsiteFlags = siteData => {
  return siteData?.siteAttrs
    ? Object.keys(siteData?.siteAttrs).reduce(
        (acc, version) => ({
          ...acc,
          [version]: fromSiteState(siteData?.siteAttrs?.[version]),
        }),
        {}
      )
    : {};
};

/**
 * function to check whether any of site version is published or not
 * @param {object} siteData - { [siteVersion]: bitMask }
 * @returns boolean
 */
export const isNeoWebsitePublished = siteData => {
  const siteVersionAttrMap = getNeoWebsiteFlags(siteData);
  return !!Object.values(siteVersionAttrMap).find(s => s.isPublished);
};

export const getNeoSiteBuilderUrlProps = () => {
  const isCustomerAccount = getLoggedInUserRole() === ADMIN_ROLE.DOMAIN_ADMIN;
  const accountId = getAccountId();
  const url = process.env.VUE_APP_NEO_SITE_BUILDER_URL;
  const token = getAuthToken();
  const name = getLoggedInUserName();
  const currentAccountEmail = getCurrentUserEmail();
  const loggedInUserProps = isCustomerAccount
    ? { customer_id: accountId } // If the user is a customer, the 'accountId' value represents the 'customerId'. In this case, the key is set as 'customer_id'
    : { account_id: accountId, accountId }; // If the user is an admin, the 'accountId' value is the actual 'accountId'. Here, the key is set as 'account_id'

  return {
    isCustomerAccount,
    url,
    token,
    currentAccountEmail,
    name,
    ...returnObjectIfValueExists("soid", store.getters?.["site/orderId"]),
    ...returnObjectIfValueExists("bid", store.getters?.["bundle/bundleId"]),
    ...loggedInUserProps,
  };
};

export const getNeoSiteBuilderUrl = (domain, action) => {
  const { isCustomerAccount, url, token, name, currentAccountEmail, ...loggedInUserProps } =
    getNeoSiteBuilderUrlProps();
  const isPublishAction = action === "publish";
  return getUrl(isPublishAction ? `${url}edit/` : url, {
    domain,
    bid: getBundleId(),
    ct: token,
    isCustomerAccount,
    source: "admin_panel",
    name,
    currentAccountEmail,
    ...(isPublishAction
      ? {
          action,
          skipPublishConfirmation: true, // to skip confirmation dialog in site builder
        }
      : {}),
    ...loggedInUserProps,
  });
};

export const openNeoSite = domain => {
  window.open(
    getNeoSiteBuilderUrl(domain),
    isMobile() ? "_blank" : "FlockSite" // in mobile the window name approach doesn't change focus to `FlockSite` tab
  );
};

export const openNeoSiteAndPublish = domain => {
  const siteTabRef = window.open(
    getNeoSiteBuilderUrl(domain, "publish"),
    isMobile() ? "_blank" : "FlockSite" // in mobile the window name approach doesn't change focus to `FlockSite` tab
  );
  if (!isMobile()) siteTabRef?.location?.reload();
};

export const openNeoSignupAppSitePurchaseFlow = (params = {}) => {
  const url = getNeoSignupAppUrl("site/industry", params);
  openURL(url, true);
};

export const openNeoSignupApp = (params = {}, path = "") => {
  const url = getNeoSignupAppUrl(path, params);
  openURL(url, true);
};

export const openNeoSiteBuilderForBundle = (bundleDetails, openInSameTab = false) => {
  const { isCustomerAccount, url, token, ...loggedInUserProps } = getNeoSiteBuilderUrlProps();

  const { domainName, id, orders } = bundleDetails;

  const siteOrder = getOrderDetailsOfProduct(orders, PRODUCTS.SITE);
  const domainOrder = getOrderDetailsOfProduct(orders, PRODUCTS.DOMAIN);

  const redirectUrl = getUrl(url, {
    ...loggedInUserProps,
    domain: domainName,
    ct: token,
    isCustomerAccount,
    source: "admin_panel",
    bid: id,
    soid: siteOrder?.id,
    domainId: domainOrder?.id,
  });

  const target = openInSameTab ? "_self" : isMobile() ? "_blank" : "FlockSite"; // in mobile the window name approach doesn't change focus to `FlockSite` tab

  window.open(redirectUrl, target);
};
